import { Component, EventEmitter, OnInit, Input, Output, ViewEncapsulation, signal } from '@angular/core';
import { ADPResourceService } from 'digital-core';
import { DeviceDto } from 'src/app/core/services/device-inventory/device.dto';

export const enum DeviceCardTemplate {
  home_device = 'home-device',
  phone_device = 'phone-device',
  cellular_device = 'cellular_device',
  wifi_device = 'wifi_device',
}

export enum DeviceCardDiagnosticTestStatus {
  PASSED = 'PASSED',
  FAILED = 'FAILED',
  PENDING = 'PENDING',
  BYPASSED = 'BYPASSED',
}

export interface IDiagnosticDeviceDetails {
  isDeviceRegistered: boolean;
  deviceTestStatus: DeviceCardDiagnosticTestStatus;
  lineNumber?: string;
  viewAction?: string;
}

export interface IDeviceCardDetails {
  device: DeviceDto;
  title?: string;
  actionLink: string;
  id?: string;
  ariaLabel: string;

  /**
   * The name of the icon to show in this device card.
   *
   * Default icon is the tablet icon.
   */
  iconName?: string;

  diagnosticDeviceDetails?: IDiagnosticDeviceDetails;
  sortOrder?: any;
  template?: string;
}

export const enum DeviceCardCategory {
  refrigerator = 'refrigerator-2tone',
  dryer = 'dryer-2tone',
  washer = 'washer-2tone',
  washer_dryer_combo = 'combo-2tone',
  ranges = 'ranges-2tone',
  cooktop = 'cooktop-2tone',
  wall_oven = 'wall-oven-2tone',
  dishwasher = 'dishwasher-2tone',
  microwave = 'microwave-2tone',
  cleaner = 'cleaner-2tone',
  small_appliance = 'small-appliance-2tone',
  camera = 'camera-2tone',
  camcorder = 'camcorder-2tone',
  drone = 'drone-2tone',
  car_stereo_receiver = 'car-stereo-receiver-2tone',
  gps_navigation = 'gps-navigation-2tone',
  car_security = 'car-security-2tone',
  back_up_dash_camera = 'back-up-dash-camera-2tone',
  car_video_media_player = 'car-video-media-player-2tone',
  laptop = 'laptop-2tone',
  desktop = 'desktop-2tone',
  monitor = 'monitor-2tone',
  storage = 'storage-2tone',
  computer_accessories = 'computer-accessories-2tone',
  exercise_equipment = 'exercise_equipment-2tone',
  smart_scale = 'smart-scale-2tone',
  home_theater_system = 'home-theater-system-2tone',
  sound_system = 'sound-system-2tone',
  bluray_player = 'bluray-player-2tone',
  projector = 'projector-2tone',
  streaming_dongle = 'streaming-2tone',
  smartphones = 'smartphones-2tone',
  printer = 'printer-2tone',
  bluetooth_speaker = 'bluetooth-speakers-2tone',
  smart_speaker = 'smart-speakers-2tone',
  smart_display = 'smart-display-2tone',
  light = 'light-2tone',
  smart_plug = 'smart-plug-2tone',
  smart_fan = 'smart-fan-2tone',
  light_switch = 'light-switch-2tone',
  thermostat = 'thermostat-2tone',
  smart_sprinkler = 'smart-sprinkler-2tone',
  home_security = 'home-security-2tone',
  bluetooth_trackers = 'bluetooth-trackers-2tone',
  tablet = 'tablet-2tone',
  smart_tv = 'smart-tv-2tone',
  console = 'console-2tone',
  game_accessories = 'game-accessories-2tone',
  watch = 'watch-2tone',
  vr_headset = 'vr-headset-2tone',
  fitness_tracker = 'fitness-tracker-2tone',
  doorbell = 'doorbell-2tone',
  sunglasses = 'sunglasses-2tone',
  headphones = 'headphones-2tone',
  wifi = 'wifi-2tone',
  modems_gateways = 'modem-gateway-2tone',
  extender = 'extender-2tone',
  adaptor = 'adaptor-2tone',
  hotspot = 'hotspot-2tone',
  generic = 'generic-boarder-2tone',
}

@Component({
  selector: 'pgh-device-card',
  templateUrl: './device-card.component.html',
  styleUrls: ['./device-card.component.scss'],
})
export class DeviceCardComponent implements OnInit {
  @Input() public deviceDetails?: IDeviceCardDetails;
  @Input() public isAddDeviceCard?: boolean = false;
  @Input() public isDeviceDetailsCard?: boolean = false;
  @Input() public isSelectDeviceView?: boolean = false;
  @Input() public hasBoxShadow?: boolean = true;
  @Input() public hasEdit?: boolean = false;
  @Input() public hasDelete?: boolean = false;
  @Input() public hasDeleteConfirmation?: boolean = false;
  @Input() public backgroundColor?: string = '';
  @Output() public handleEditAction = new EventEmitter<IDeviceCardDetails>();
  @Output() public handleDeleteAction = new EventEmitter<IDeviceCardDetails>();
  @Output() public handleViewAction = new EventEmitter<IDeviceCardDetails>();
  @Output() public handleAddAction = new EventEmitter<string>();
  @Output() public handleSelectDeviceAction = new EventEmitter<IDeviceCardDetails>();

  public icon: string;
  public title: string;
  public actionLink: string;
  public id: string;
  public ariaLabel: string;
  public editIconAriaLabel: string;
  public deleteIconAriaLabel: string;
  public isBackgroundColorOverride: boolean = false;
  public isPhoneTemplate: boolean = false;
  public isPhoneRegistered: boolean = false;
  public isCellularEnabledTemplate = false;
  public phoneNumber: string;
  public phoneTestStatus: string;
  public phoneTestFailed: boolean = false;

  public cardStyle: string = '';

  public actionViewDetailsLabel: string = '';
  public actionClickForDetailsLabel: string = '';
  public actionViewTestResultsLabel: string = '';
  public actionManageInviteLabel: string = '';

  public deviceIMEINumber: string = '';

  public isEprismDevice: boolean = false;

  urlClaimsSupport = signal('/claims-support');

  constructor(private resourceService: ADPResourceService) {}

  public ngOnInit(): void {

    if(this.isAddDeviceCard){
      this.title = ""
      this.actionLink = this.resourceService.getPortaLabelValue("DeviceInventory_SPOE_AddDevice_Label");
      this.id = null;
      this.ariaLabel = this.resourceService.getPortaLabelValue("DeviceInventory_SPOE_AddDevice_Label");
    }
    else{
      this.actionLink = this.deviceDetails.actionLink;
      this.title = this.deviceDetails.title;
      this.id = this.deviceDetails.id;
      this.ariaLabel = this.deviceDetails.ariaLabel;
    }

    this.editIconAriaLabel = this.resourceService.getPortalLabel('DeviceCard_Action_EditIcon_Aria_Label').Value;
    this.deleteIconAriaLabel = this.resourceService.getPortalLabel('DeviceCard_Action_DeleteIcon_Aria_Label').Value;

    this.actionViewDetailsLabel = this.resourceService
      .getPortalLabel('DeviceCard_Action_ViewDetails_Label')
      .Value.replace('<div>', '')
      .replace('</div>', '');
    this.actionClickForDetailsLabel = this.resourceService
      .getPortalLabel('DeviceCard_Action_ClickForDetails_Label')
      .Value.replace('<div>', '')
      .replace('</div>', '');
    this.actionViewTestResultsLabel = this.resourceService
      .getPortalLabel('DeviceCard_Action_ViewTestResults_Label')
      .Value.replace('<div>', '')
      .replace('</div>', '');

    if(!this.isAddDeviceCard){
      if (this.deviceDetails.template === DeviceCardTemplate.home_device) {
        this.icon = this.deviceDetails.iconName ?? 'generic-boarder-2tone';
        this.icon = this.icon.replace('_', '-');

        if(this.deviceDetails.device.deviceSource === 'EPRISM'){
          this.isEprismDevice = true;

          if(this.deviceDetails.device.deviceType.code === 'SMPH'){
            // replace the multi phone icon to the single phone icon for EPRISM phone devices
            this.icon = "smartphone-2tone";
          }

          this.isPhoneTemplate = true;
          this.phoneNumber = this.formatPhoneNumber(this.deviceDetails.device.phoneNumber);
          this.isPhoneRegistered = true;
          this.phoneTestStatus = DeviceCardDiagnosticTestStatus.PASSED;

          if (this.isSelectDeviceView) {
            this.actionLink = this.phoneNumber;
          }
        }

        // if Make and Model are 'blank'/null/or undefined, replace the action link title in the device card to 'Click for Details'
        if (
          (this.deviceDetails.device.make === undefined ||
            this.deviceDetails.device.make === null ||
            this.deviceDetails.device.make.trim() === '') &&
          (this.deviceDetails.device.model === undefined ||
            this.deviceDetails.device.model === null ||
            this.deviceDetails.device.model.trim() === '')
        ) {
          this.actionLink = this.actionViewDetailsLabel;
        }

        if (!this.isSelectDeviceView && !this.isAddDeviceCard) {
          this.actionLink = this.actionViewDetailsLabel;
        }
      } else if (this.deviceDetails.template === DeviceCardTemplate.cellular_device) {
        this.actionManageInviteLabel = this.resourceService
          .getPortalLabel('DeviceCard_Action_RescanQRCode_Label')
          .Value.replace('<div>', '')
          .replace('</div>', '');

        this.icon = this.deviceDetails.iconName ?? 'generic-boarder-2tone';
        this.icon = this.icon.replace('_', '-');
        // NOTE: Removing the distinctive cellular-enabled icon design for now as UX is going back and forth on whether to show this or not
        //this.icon = this.icon + '-cellular';
        this.isCellularEnabledTemplate = true;
        this.isPhoneRegistered = this.deviceDetails.diagnosticDeviceDetails.isDeviceRegistered;
        this.phoneTestStatus = this.deviceDetails.diagnosticDeviceDetails.deviceTestStatus;

        this.deviceIMEINumber = this.deviceDetails.device.imei;

        // TODO Need to handle serial number when added to DeviceDto

        if (this.isPhoneRegistered) {
          // if Make and Model are 'blank'/null/or undefined, replace the action link title in the device card to 'Click for Details'
          if (
            (this.deviceDetails.device.make === undefined ||
              this.deviceDetails.device.make === null ||
              this.deviceDetails.device.make.trim() === '') &&
            (this.deviceDetails.device.model === undefined ||
              this.deviceDetails.device.model === null ||
              this.deviceDetails.device.model.trim() === '')
          ) {
            this.actionLink = this.actionViewDetailsLabel;
          }

          if (!this.isSelectDeviceView) {
            this.actionLink = this.actionViewDetailsLabel;
          }
        } else {
          if (this.phoneTestStatus === DeviceCardDiagnosticTestStatus.PENDING) {
            this.actionLink = this.actionManageInviteLabel;
            this.phoneTestFailed = false;
          } else if (this.phoneTestStatus === DeviceCardDiagnosticTestStatus.FAILED) {
            this.actionLink = this.actionViewTestResultsLabel;
            this.phoneTestFailed = true;
          }
        }
      } else if (this.deviceDetails.template === DeviceCardTemplate.phone_device) {
        this.actionManageInviteLabel = this.resourceService
          .getPortalLabel('DeviceCard_Action_ResendInvite_Label')
          .Value.replace('<div>', '')
          .replace('</div>', '');
        this.isPhoneTemplate = true;
        this.phoneNumber = this.deviceDetails.diagnosticDeviceDetails.lineNumber;
        this.isPhoneRegistered = this.deviceDetails.diagnosticDeviceDetails.isDeviceRegistered;
        this.phoneTestStatus = this.deviceDetails.diagnosticDeviceDetails.deviceTestStatus;

        // using same icon for both states now
        this.icon = 'mobile-phones-not-registered';

        if (this.isPhoneRegistered) {
          // if Make and Model are 'blank'/null/or undefined, replace the action link title in the device card to 'Click for Details'
          if (
            (this.deviceDetails.device.make === undefined ||
              this.deviceDetails.device.make === null ||
              this.deviceDetails.device.make.trim() === '') &&
            (this.deviceDetails.device.model === undefined ||
              this.deviceDetails.device.model === null ||
              this.deviceDetails.device.model.trim() === '')
          ) {
            this.actionLink = this.actionViewDetailsLabel;
          }

          if (!this.isSelectDeviceView) {
            this.actionLink = this.actionViewDetailsLabel;
          }
        } else {
          if (this.phoneTestStatus === DeviceCardDiagnosticTestStatus.PENDING) {
            this.actionLink = this.actionManageInviteLabel;
            this.phoneTestFailed = false;
          } else if (this.phoneTestStatus === DeviceCardDiagnosticTestStatus.FAILED) {
            this.actionLink = this.actionViewTestResultsLabel;
            this.phoneTestFailed = true;
          }
        }
      }
    }

    if (this.backgroundColor) {
      this.isBackgroundColorOverride = true;
    }
  }

  public handleViewActionClick(): void {
    if (this.actionLink === this.actionManageInviteLabel) {
      if (this.deviceDetails != undefined) {
        if (this.deviceDetails.diagnosticDeviceDetails != undefined) {
          this.deviceDetails.diagnosticDeviceDetails.viewAction = this.actionManageInviteLabel;
        }
      }
    } else if (this.actionLink === this.actionViewTestResultsLabel) {
      if (this.deviceDetails != undefined) {
        if (this.deviceDetails.diagnosticDeviceDetails != undefined) {
          this.deviceDetails.diagnosticDeviceDetails.viewAction = this.actionViewTestResultsLabel;
        }
      }
    } else {
      if (this.deviceDetails != undefined) {
        if (this.deviceDetails.diagnosticDeviceDetails != undefined) {
          this.deviceDetails.diagnosticDeviceDetails.viewAction = this.actionViewDetailsLabel;
        }
      }
    }

    this.handleViewAction.emit(this.deviceDetails);
  }

  public handleDeleteActionClick(): void {
    if (this.hasDelete) {
      this.handleDeleteAction.emit(this.deviceDetails);
    }
  }

  public handleEditActionClick(): void {
    if (this.hasEdit) {
      this.handleEditAction.emit(this.deviceDetails);
    }
  }

  public handleMouseDownClick(): void {
    this.cardStyle = 'card-clicked';
  }

  public handleMouseUpClick(): void {
    this.cardStyle = 'card-default clickable';
  }

  public handleSelectActionClick(): void {
    this.handleSelectDeviceAction.emit(this.deviceDetails);
  }

  public handleAddActionClick(): void {
    this.handleAddAction.emit('Handle Add Device Action');
  }

  public formatPhoneNumber(phoneNumberString: string): string {
    const cleaned = ('' + phoneNumberString).replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
    return phoneNumberString;
  }
}
